import React from 'react';
import { Project } from '../../../types';

interface ProjectPropertiesProps {
  project: Project;
  canEdit: boolean;
  onUpdate: (field: string, value: any) => void;
  getStatusColor: (status: string) => string;
}

const ProjectProperties: React.FC<ProjectPropertiesProps> = ({
  project,
  canEdit,
  onUpdate,
  getStatusColor,
}) => {
  return (
    <div className="grid grid-cols-2 gap-x-8 gap-y-4 mb-8 text-sm">
      {/* Status */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-gray-600 w-32">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Status</span>
        </div>
        {canEdit ? (
          <select
            value={project.status}
            onChange={(e) => onUpdate('status', e.target.value)}
            className={`px-3 py-1 text-sm font-medium rounded cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 ${getStatusColor(project.status)}`}
          >
            <option value="planning">Planning</option>
            <option value="development">Development</option>
            <option value="testing">Testing</option>
            <option value="sit">SIT</option>
            <option value="uat">UAT</option>
            <option value="done">Done</option>
            <option value="production">Production</option>
            <option value="archived">Archived</option>
          </select>
        ) : (
          <span className={`px-3 py-1 text-sm font-medium rounded ${getStatusColor(project.status)}`}>
            {project.status.toUpperCase()}
          </span>
        )}
      </div>

      {/* Priority */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-gray-600 w-32">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
          </svg>
          <span>Priority</span>
        </div>
        {canEdit ? (
          <select
            value={project.priority}
            onChange={(e) => onUpdate('priority', e.target.value)}
            className="px-3 py-1 text-sm font-medium rounded cursor-pointer border border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        ) : (
          <span className="px-3 py-1 text-sm">{project.priority.toUpperCase()}</span>
        )}
      </div>

      {/* Owner */}
      <div className="flex items-center gap-3 col-span-2">
        <div className="flex items-center gap-2 text-gray-600 w-32">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>Owner</span>
        </div>
        <div className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded text-sm">
          <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium">
            {project.owner.name.charAt(0).toUpperCase()}
          </div>
          <span>{project.owner.name}</span>
        </div>
      </div>

      {/* Start Date */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-gray-600 w-32">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>Start Date</span>
        </div>
        {canEdit ? (
          <input
            type="date"
            value={project.dates.startDate ? new Date(project.dates.startDate).toISOString().split('T')[0] : ''}
            onChange={(e) => onUpdate('dates.startDate', e.target.value)}
            className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <span className="text-sm">{project.dates.startDate ? new Date(project.dates.startDate).toLocaleDateString() : 'Not set'}</span>
        )}
      </div>

      {/* End Date */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-gray-600 w-32">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>End Date</span>
        </div>
        {canEdit ? (
          <input
            type="date"
            value={project.dates.estimatedEndDate ? new Date(project.dates.estimatedEndDate).toISOString().split('T')[0] : ''}
            onChange={(e) => onUpdate('dates.estimatedEndDate', e.target.value)}
            className="px-3 py-1 text-sm border border-gray-200 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <span className="text-sm">{project.dates.estimatedEndDate ? new Date(project.dates.estimatedEndDate).toLocaleDateString() : 'Not set'}</span>
        )}
      </div>

      {/* Visibility */}
      {project.visibility && (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-gray-600 w-32">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span>Visibility</span>
          </div>
          {canEdit ? (
            <select
              value={project.visibility}
              onChange={(e) => onUpdate('visibility', e.target.value)}
              className={`px-3 py-1 text-sm font-medium rounded cursor-pointer border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 ${project.visibility === 'public' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          ) : (
            <span className={`px-2 py-1 text-xs rounded ${project.visibility === 'public' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
              {project.visibility.toUpperCase()}
            </span>
          )}
        </div>
      )}

      {/* Category */}
      {project.category && (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-gray-600 w-32">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span>Category</span>
          </div>
          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
            {project.category}
          </span>
        </div>
      )}
    </div>
  );
};

export default ProjectProperties;
